import { MERCHANTS, EXTRA_SOURCES } from "./index";
import { safeLower, safeTrim } from "@/lib/safeString";
import type { MerchantConfig, MerchantId, SourceConfig, ExtraSource } from "./types";
import type { CategoryKey } from "../categories";

// Get all merchants as array
export function getAllMerchants(): MerchantConfig[] {
  return Object.values(MERCHANTS);
}

// Get merchants by category key
export function getMerchantsByCategory(category: CategoryKey): MerchantConfig[] {
  return getAllMerchants().filter((m) => m.categoryKey === category);
}

// Get a single merchant by ID
export function getMerchant(id: MerchantId): MerchantConfig | undefined {
  return MERCHANTS[id];
}

// Get all sources for a merchant
export function getMerchantSources(id: MerchantId, sourceType?: SourceConfig["sourceType"]): SourceConfig[] {
  const merchant = MERCHANTS[id];
  if (!merchant) return [];
  if (sourceType) return merchant.sources.filter((s) => s.sourceType === sourceType);
  return merchant.sources;
}

// Get merchants with sources
export function getMerchantsWithSources(): MerchantConfig[] {
  return getAllMerchants().filter((m) => m.sources.length > 0);
}

// Get all merchant display names
export function getAllMerchantDisplayNames(): string[] {
  return getAllMerchants().map((m) => m.displayName);
}

/**
 * CR406: Find merchant by display name - hardened with safeLower/safeTrim
 * Logs debug payload for troubleshooting when matches fail
 */
export function findMerchantByName(name: unknown): MerchantConfig | undefined {
  // CR406: Safe string normalization - never throws on undefined/null
  const normalized = safeLower(safeTrim(name));
  
  if (!normalized) {
    console.debug("[findMerchantByName] Empty or invalid name provided:", { input: name });
    return undefined;
  }
  
  const match = getAllMerchants().find((m) => {
    const displayNameNorm = safeLower(m.displayName);
    const idNorm = safeLower(m.id?.replace(/-/g, " "));
    return displayNameNorm === normalized || idNorm === normalized;
  });
  
  // CR406: Debug logging for troubleshooting
  if (!match && import.meta.env.DEV) {
    console.debug("[findMerchantByName] No match found:", { 
      input: name, 
      normalized,
      merchantCount: getAllMerchants().length 
    });
  }
  
  return match;
}

// Get all extra sources
export function getAllExtraSources(): ExtraSource[] {
  return [...EXTRA_SOURCES].sort((a, b) => a.priority - b.priority);
}
